import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';

type CertificationContractResponse =
  operations['getApiV1CertificationsByCertificationId']['responses'][200]['content']['application/json'];
type DocumentsContractResponse =
  operations['getApiV1Documents']['responses'][200]['content']['application/json'];
type EducationModuleLogContractResponse =
  operations['getUserProfileEducationModuleLog']['responses'][200]['content']['application/json'];
type PaymentsContractResponse =
  operations['getApiV1Payments']['responses'][200]['content']['application/json'];
type RegistrationContractResponse =
  operations['getApiV1RegistrationsByRegistrationId']['responses'][200]['content']['application/json'];

const nullableText = z.string().nullable();
const uuid = z.uuid();
const relatedRecordSchema = z.object({ id: uuid, name: nullableText });
const optionSchema = z.object({
  dbValue: nullableText,
  display: nullableText,
  id: uuid.nullable(),
});
const amountSchema = z.object({ amount: nullableText, currency: z.literal('USD') });
const countSchema = z.object({
  completed: z.number().int().nonnegative().nullable(),
  total: z.number().int().nonnegative().nullable(),
});
const progressItemSchema = z.object({ moduleId: uuid.nullable(), result: nullableText });

export const crmLearningProfileRegistrationResponseSchema = z.object({
  data: z.object({
    course: z
      .object({
        id: uuid.nullable().optional(),
        name: nullableText.optional(),
        number: nullableText.optional(),
        organizationName: nullableText,
        type: z
          .object({ badgeImageUrl: nullableText, hasBanner: z.boolean(), name: nullableText })
          .nullable(),
      })
      .nullable(),
    courseType: z
      .object({ badgeImageUrl: nullableText, hasBanner: z.boolean(), id: uuid, name: nullableText })
      .nullable()
      .optional(),
    cost: amountSchema.optional(),
    examPassedAt: nullableText.optional(),
    id: uuid,
    instructorPayment: amountSchema.extend({ status: optionSchema.nullable() }).optional(),
    paymentStatus: optionSchema.nullable().optional(),
    progress: z
      .object({
        certification: z.object({ completed: z.boolean().nullable(), id: uuid.nullable() }),
        completionForm: z.object({
          completed: z.boolean().nullable(),
          createdAt: nullableText,
          id: uuid.nullable(),
          instructor: relatedRecordSchema.nullable(),
          instructorSignature: z.boolean().nullable(),
          paperCcfAvailable: z.boolean().nullable(),
          requirements: countSchema,
        }),
        examResult: nullableText,
        mainOnlineExam: progressItemSchema,
        mainOnlineModule: progressItemSchema,
        onlineExams: countSchema,
        onlineModules: countSchema,
      })
      .optional(),
    registrationNumber: nullableText.optional(),
    registrationType: optionSchema.nullable().optional(),
    student: z.object({ displayName: nullableText, imageUrl: nullableText }).nullable(),
    studentPayment: amountSchema.extend({ status: optionSchema.nullable() }).optional(),
    submittedAt: nullableText.optional(),
    userProfileId: uuid.nullable().optional(),
  }),
}) satisfies z.ZodType<RegistrationContractResponse>;

export type CrmLearningProfileRegistrationResponse = z.infer<
  typeof crmLearningProfileRegistrationResponseSchema
>;

export const crmLearningProfileCertificationResponseSchema = z.object({
  data: z.object({
    certificatePreview: z.discriminatedUnion('state', [
      z.object({
        href: z.string().startsWith('/api/v1/certifications/'),
        state: z.literal('ready'),
      }),
      z.object({ reason: z.enum(['missing', 'processing']), state: z.literal('unavailable') }),
    ]),
    certifiedAt: nullableText,
    code: nullableText,
    completed: z.boolean().nullable(),
    courseType: relatedRecordSchema.nullable(),
    creator: relatedRecordSchema.nullable(),
    examPassedAt: nullableText,
    id: uuid,
    instructor: relatedRecordSchema.nullable(),
    registration: z.object({ id: uuid, number: nullableText }).nullable(),
    type: relatedRecordSchema.extend({ badgeUrl: nullableText }).nullable(),
    user: z.object({
      avatarUrl: nullableText,
      birthDate: nullableText,
      btLevel: relatedRecordSchema.nullable(),
      fieldVisibility: z.object({
        avatar: z.enum(['available', 'redacted', 'unavailable']),
        birthDate: z.enum(['available', 'redacted', 'unavailable']),
      }),
      freediverNumber: nullableText,
      id: uuid,
      name: nullableText,
    }),
  }),
}) satisfies z.ZodType<CertificationContractResponse>;

export type CrmLearningProfileCertificationResponse = z.infer<
  typeof crmLearningProfileCertificationResponseSchema
>;

const historyDetailSchema = z.object({
  attempted_at: nullableText,
  attempt_policy: z.object({ maximum_attempts: z.number().int().positive().nullable() }),
  course_type: z.object({ badge_image_url: nullableText, id: uuid, name: nullableText }).nullable(),
  created_manually: z.boolean().nullable(),
  current_slide_index: z.number().int().nonnegative().nullable(),
  id: uuid,
  instructor: relatedRecordSchema.nullable(),
  module: z
    .object({ id: uuid, image_url: nullableText, title: nullableText, type: nullableText })
    .nullable(),
  module_id: uuid.nullable(),
  notes: nullableText,
  profile_ref: uuid,
  result: nullableText,
  score: z.object({
    basis_points: z.number().int().min(0).max(10_000).nullable(),
    points: z.number().nonnegative().nullable(),
    points_possible: z.number().nonnegative().nullable(),
  }),
  threshold_basis_points: z.number().int().min(0).max(10_000).nullable(),
  type: nullableText,
  user: relatedRecordSchema,
});

/** UUID profile routes are the CRM history projection; runtime shapes are rejected at this boundary. */
export const crmLearningProfileEducationModuleLogResponseSchema = z.object({
  data: historyDetailSchema,
}) satisfies z.ZodType<EducationModuleLogContractResponse>;

export type CrmLearningProfileEducationModuleLogResponse = z.infer<
  typeof crmLearningProfileEducationModuleLogResponseSchema
>;

export const crmLearningProfileDocumentsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        createdAt: nullableText,
        expiresAt: nullableText,
        fileState: z.enum([
          'available',
          'processing',
          'temporarily-unavailable',
          'no-file',
          'data-issue',
        ]),
        format: z.enum(['pdf', 'image', 'doc', 'docx', 'unknown']),
        id: uuid,
        isExpired: z.boolean(),
        lifecycleState: z.enum(['synchronized', 'pending_sync', 'dispatching', 'failed']),
        provenance: z.object({
          lastSyncedAt: nullableText,
          sourceObject: z.literal('user_doc'),
          sourceSystem: z.enum(['bubble', 'move', 'unknown']),
        }),
        registrationId: uuid.nullable(),
        status: z.enum(['in-review', 'approved', 'not-valid', 'draft', 'cancelled', 'unknown']),
        type: z.enum([
          'certification-card',
          'paper-exam',
          'paper-completion-form',
          'first-aid-certificate',
          'medical-questionnaire',
          'student-liability-release',
          'instructor-insurance-waiver',
          'unknown',
        ]),
        uploadMode: z.enum(['file', 'digital-form', 'unknown']),
        updatedAt: nullableText,
        userProfileId: uuid,
      })
    ),
  }),
  meta: z.object({
    countMode: z.literal('exact'),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    page: z.number().int().positive(),
    pageSize: z.union([z.literal(5), z.literal(10)]),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
}) satisfies z.ZodType<DocumentsContractResponse>;

export type CrmLearningProfileDocumentsResponse = z.infer<
  typeof crmLearningProfileDocumentsResponseSchema
>;

const codeLabelSchema = z.object({ code: z.string(), label: z.string() });
export const crmLearningProfilePaymentsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        accountDisplayName: nullableText,
        amountPaidUsd: z.string(),
        cart: z.object({ id: uuid, itemCount: z.number().int().nonnegative() }).nullable(),
        courseId: nullableText,
        creditDirection: nullableText,
        creditReceivedBy: nullableText,
        currency: z.literal('USD').optional(),
        hasExpandableContent: z.boolean(),
        id: uuid,
        itemTypes: z.array(codeLabelSchema),
        legacyDescription: nullableText,
        membershipId: uuid.nullable(),
        origin: codeLabelSchema.nullable(),
        paymentDate: z.string(),
        paymentNumber: nullableText.optional(),
        paymentType: codeLabelSchema,
        processorText: nullableText,
        registrationIds: z.array(uuid),
        status: codeLabelSchema,
        trainingProgramIds: z.array(uuid),
        userProfileId: uuid.nullable(),
      })
    ),
    pageInfo: z.object({ hasNextPage: z.boolean(), nextCursor: nullableText }),
    summary: z.object({ totalAmountUsd: z.string(), totalItems: z.number().int().nonnegative() }),
  }),
}) satisfies z.ZodType<PaymentsContractResponse>;

export type CrmLearningProfilePaymentsResponse = z.infer<
  typeof crmLearningProfilePaymentsResponseSchema
>;
